import React, { useState } from "react";
import logo from "../assets/scenathon_logo_short.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import iconResultados from "../assets/icons/clipboard-text.svg";
import iconPathways from "../assets/icons/pathways.svg";
import iconScenarios from "../assets/icons/scenarios.svg";
import iconScenathon from "../assets/icons/scenathon.svg";
import iconLogout from "../assets/icons/logout.svg";
import iconAdmin from "../assets/icons/admin.svg";
import iconTrade from "../assets/icons/tradeIcon.svg";
import iconChat from "../assets/chatbot.png";
import { FiRefreshCw } from "react-icons/fi";
import { useAuth } from "../auth/AuthContext";
import { useRole } from "../auth/useRole";
import { ShowIfHasSection, ShowIfRole } from "../auth/RoleComponents";

const API = process.env.REACT_APP_API_URL;
const SIDEBAR_W = "w-[302px]";

export default function Sidebar() {
  const [open, setOpen] = useState(false);
  const [restartingCalculator, setRestartingCalculator] = useState(false);
  const [restartStatus, setRestartStatus] = useState("");

  const location = useLocation();
  const { SECTIONS } = useRole();
  const navigate = useNavigate();
  const { logout, accessToken, csrfToken, selectedScenathon, authFetch } = useAuth();

  const isActive = (path) => location.pathname.startsWith(path);
  const close = () => setOpen(false);

  const handleLogout = async () => {
    try {
      await logout?.();
    } catch (e) {
      console.warn("AuthContext logout failed:", e);
      try {
        await fetch(`${API}/api/auth/logout`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrfToken ? { "y-csrf-token": csrfToken } : {}),
            ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
          },
          body: JSON.stringify({}),
        });
      } catch (err) {
        console.warn("Logout endpoint failed:", err);
      }
    } finally {
      close();
      navigate("/", { replace: true });
    }
  };

  const handleRestartCalculator = async () => {
    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Restart Calculator?",
      text: "This will delete your calculator data and sign you out.",
      showCancelButton: true,
      confirmButtonText: "Restart",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#b91c1c",
    });
    if (!confirmation.isConfirmed) return;

    close();
    setRestartingCalculator(true);
    setRestartStatus("Creating logout task...");

    try {
      const startResponse = await authFetch("/api/auth/restart-calculator/start", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const startData = await startResponse.json().catch(() => ({}));
      if (!startResponse.ok) {
        throw new Error(startData?.error || `Error creating task (${startResponse.status})`);
      }

      const tasktodoId = startData?.tasktodoId;
      if (!tasktodoId) throw new Error("The server did not return a task id.");

      while (true) {
        const statusResponse = await authFetch(
          `/api/sendtask/tasktodo/${tasktodoId}/status`,
          { method: "GET" },
        );
        const statusData = await statusResponse.json().catch(() => ({}));
        if (!statusResponse.ok) {
          throw new Error(statusData?.error || `Error checking task (${statusResponse.status})`);
        }

        const status = String(statusData?.status || "").trim().toLowerCase();
        setRestartStatus(`Logout task: ${status || "pending"}`);
        if (status === "done") break;
        if (["error", "failed", "cancelled"].includes(status)) {
          throw new Error(`Logout task ended with status: ${status}`);
        }

        await new Promise((resolve) => setTimeout(resolve, 2000));
      }

      setRestartStatus("Deleting calculator data...");
      const finishResponse = await authFetch("/api/auth/restart-calculator/finish", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tasktodoId }),
      });
      const finishData = await finishResponse.json().catch(() => ({}));
      if (!finishResponse.ok) {
        throw new Error(finishData?.error || `Error clearing data (${finishResponse.status})`);
      }

      await logout?.({ skipRequest: true });
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Error restarting calculator:", error);
      Swal.fire({
        icon: "error",
        title: "Restart Calculator",
        text: error.message || "Could not restart the calculator.",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setRestartingCalculator(false);
      setRestartStatus("");
    }
  };

  const linkCls = (path) =>
    `flex items-center gap-3 text-left py-3 px-4 rounded-md font-medium transition duration-150 ${isActive(path) ? "bg-[#03203d] text-white" : "hover:bg-gray-100"
    }`;

  const iconCls = (path) =>
    `w-5 h-5 ${isActive(path) ? "invert brightness-0" : ""}`;

  return (
    <>
      {/* ✅ Hamburger SOLO mobile y se oculta al abrir */}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`lg:hidden fixed top-4 left-4 z-[60] h-10 w-10 rounded-lg bg-white shadow ring-1 ring-gray-200 items-center justify-center ${open ? "hidden" : "flex"
          }`}
        aria-label="Open menu"
      >
        <span className="text-xl leading-none">☰</span>
      </button>

      {/* ✅ Overlay SOLO mobile */}
      <div
        onClick={close}
        className={`lg:hidden fixed inset-0 z-50 bg-black/40 transition-opacity ${open ? "opacity-100" : "pointer-events-none opacity-0"
          }`}
      />

      {/* ✅ Sidebar: desktop fixed; mobile drawer */}
      <aside
        className={`
          ${SIDEBAR_W}
          bg-white rounded-md p-6 flex flex-col overflow-y-auto
          h-screen fixed top-0 left-0 z-[55]

          transform transition-transform duration-200 ease-out
          ${open ? "translate-x-0" : "-translate-x-full"}
          lg:translate-x-0
        `}
      >
        <div className="mb-8">
          <div className="flex items-center justify-between gap-4">
            <img src={logo} alt="logo" className="w-14 object-contain" />

            <button
              type="button"
              onClick={close}
              className="lg:hidden h-9 w-9 rounded-lg hover:bg-gray-100 flex items-center justify-center"
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>
        </div>

        <nav className="flex flex-col gap-3 flex-1">
          <ShowIfHasSection section={SECTIONS.PATHWAYS}>
            <Link
              to="/pathways"
              onClick={close}
              className={linkCls("/pathways")}
            >
              <img
                src={iconPathways}
                alt="icon"
                className={iconCls("/pathways")}
              />
              Pathways
            </Link>
          </ShowIfHasSection>

          <ShowIfHasSection section={SECTIONS.SCENARIOS}>
            <Link
              to="/scenarios"
              onClick={close}
              className={linkCls("/scenarios")}
            >
              <img
                src={iconScenarios}
                alt="icon"
                className={iconCls("/scenarios")}
              />
              Scenarios
            </Link>
          </ShowIfHasSection>

          <ShowIfHasSection section={SECTIONS.RESULTS}>
            <Link to="/results" onClick={close} className={linkCls("/results")}>
              <img
                src={iconResultados}
                alt="icon"
                className={iconCls("/results")}
              />
              Results
            </Link>
          </ShowIfHasSection>

          <ShowIfHasSection section={SECTIONS.COMPARISON}>
            <Link
              to="/comparison"
              onClick={close}
              className={linkCls("/comparison")}
            >
              <img
                src={iconScenarios}
                alt="icon"
                className={iconCls("/comparison")}
              />
              Comparison
            </Link>
          </ShowIfHasSection>

          <ShowIfHasSection section={SECTIONS.AI_ASSISTANT}>
          <Link
            to="/ai-assistant"
            onClick={close}
            className={linkCls("/ai-assistant")}
          >
            <img
              src={iconChat}
              alt="icon"
              className={iconCls("/ai-assistant")}
            />
            AI Assistant
          </Link>
          </ShowIfHasSection>

          <Link
            to="/scenathon"
            onClick={close}
            className={linkCls("/scenathon")}
          >
            <img
              src={iconScenathon}
              alt="icon"
              className={iconCls("/scenathon")}
            />
            Scenathon
          </Link>

          <ShowIfHasSection section={SECTIONS.TRADE_ADJUSTMENT}>
            <Link
              to="/trade-adjustment"
              onClick={close}
              className={linkCls("/trade-adjustment")}
            >
              <img
                src={iconTrade}
                alt="icon"
                className={iconCls("/trade-adjustment")}
              />
              Trade Adjustment
            </Link>
          </ShowIfHasSection>

          <ShowIfRole requiredRoles={["admin", "organizer"]}>
            <Link
              to="/fairshare"
              onClick={close}
              className={linkCls("/fairshare")}
            >
              <img
                src={iconTrade}
                alt="icon"
                className={iconCls("/fairshare")}
              />
              Fairshare
            </Link>
          </ShowIfRole>
        </nav>

        {selectedScenathon?.id && (
          <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              Scenathon active
            </p>
            <p className="mt-1 truncate font-semibold text-slate-900">
              {selectedScenathon.name || `ID ${selectedScenathon.id}`}
            </p>
          </div>
        )}

        <ShowIfHasSection section={SECTIONS.ADMIN_SCENATHON}>
          <Link to="/admin/scenathon"
            onClick={close}
            className={linkCls("/admin/scenathon")}>
            <img src={iconAdmin} alt="scenathon" className={iconCls("/admin/scenathon")} />
            Admin Scenathon</Link>
        </ShowIfHasSection>

        <ShowIfHasSection section={SECTIONS.RESTART_CALCULATOR}>
          <button
            type="button"
            onClick={handleRestartCalculator}
            disabled={restartingCalculator}
            className="flex items-center gap-3 text-left py-3 px-4 rounded-md font-medium transition duration-150 hover:bg-gray-100"
          >
            <FiRefreshCw className="h-5 w-5" aria-hidden="true" />
            Restart Calculator
          </button>
        </ShowIfHasSection>

        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-3 text-left py-3 px-4 rounded-md font-medium hover:bg-gray-100 transition duration-150"
        >
          <img src={iconLogout} alt="logout" className="w-5 h-5" />
          Logout
        </button>

      </aside>

      {restartingCalculator && (
        <div
          className="fixed inset-0 z-[100] flex items-start justify-center bg-black/50 px-4 pb-4 pt-20 backdrop-blur-sm md:pt-24"
          role="dialog"
          aria-modal="true"
          aria-live="polite"
          aria-label="Restart Calculator progress"
        >
          <div className="w-full max-w-lg rounded-xl border border-slate-200 bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Calculator
                </p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">
                  Restarting calculator
                </h2>
              </div>
              <div className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-[#03203d]" />
            </div>
            <div className="mt-5 flex items-center justify-between gap-3 text-sm">
              <span className="text-slate-600">Status</span>
              <span className="rounded-full bg-sky-50 px-3 py-1 font-medium text-sky-800">
                {restartStatus || "Starting..."}
              </span>
            </div>
            <div
              className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100"
              role="progressbar"
              aria-label="Restarting calculator"
            >
              <div className="calculator-progress-indicator h-full rounded-full bg-sky-600" />
            </div>
            <p className="mt-3 text-xs text-slate-500">
              Keep this page open while the logout task finishes.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
